import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getPortalConsentStatus } from "../api/familyPortalConsentApi";

dayjs.extend(customParseFormat);

const CONSENT_ACCEPTED_AT_FORMAT = "MM/DD/YYYY hh:mm:ss A";

/**
 * Formats `consentAcceptedAt` for display; returns "" when missing or unparseable.
 * @param {string} value
 * @param {string} [displayFormat]
 */
export const formatConsentAcceptedAt = (value, displayFormat = "MMM D, YYYY [at] h:mm A") => {
  const parsed = dayjs(String(value || "").trim(), CONSENT_ACCEPTED_AT_FORMAT, true);
  return parsed.isValid() ? parsed.format(displayFormat) : "";
};

/**
 * Family portal "Confidentiality & Authorized Access" acknowledgement.
 *
 * The database (family_member_details.portal_consent_accepted) is the only
 * source of truth. This module caches the server's answer in memory for the
 * current page load: a refresh or app start clears it, so the status is
 * re-checked with getPortalConsentStatus.do; logout clears it as well.
 */

let consentConfirmed = false;
let pendingStatusRequest = null;

export const isFamilyPortalConsentConfirmed = () => consentConfirmed;

export const markFamilyPortalConsentConfirmed = () => {
  consentConfirmed = true;
};

export const clearFamilyPortalConsentCache = () => {
  consentConfirmed = false;
  pendingStatusRequest = null;
};

/**
 * Asks the server whether the signed-in family member has accepted. Concurrent
 * callers share one request. Any failure resolves to `false` so the agreement
 * is shown rather than portal data.
 * @returns {Promise<boolean>}
 */
export const fetchFamilyPortalConsentStatus = () => {
  if (consentConfirmed) {
    return Promise.resolve(true);
  }

  if (!pendingStatusRequest) {
    pendingStatusRequest = getPortalConsentStatus()
      .then((result) => {
        const accepted = result.success && result.data?.consentAccepted === true;
        if (accepted) {
          consentConfirmed = true;
        }
        return accepted;
      })
      .catch(() => false)
      .finally(() => {
        pendingStatusRequest = null;
      });
  }

  return pendingStatusRequest;
};
